import {
  type User,
  EmailAuthProvider,
  confirmPasswordReset as firebaseConfirmPasswordReset,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updatePassword as firebaseUpdatePassword,
  updateProfile,
  verifyPasswordResetCode as firebaseVerifyPasswordResetCode,
} from 'firebase/auth';

import { getFirebaseApp, getFirebaseAuth } from './app';

interface CheckEmailExistsResponse {
  exists: boolean;
}

function isCheckEmailExistsResponse(value: unknown): value is CheckEmailExistsResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  if (!('exists' in value)) {
    return false;
  }
  return typeof value.exists === 'boolean';
}

export async function signIn(email: string, password: string): Promise<User> {
  const auth = getFirebaseAuth();
  const credential = await signInWithEmailAndPassword(auth, email, password);
  return credential.user;
}

export async function signUp(email: string, password: string): Promise<User> {
  const auth = getFirebaseAuth();
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  return credential.user;
}

export async function signOut(): Promise<void> {
  const auth = getFirebaseAuth();
  await firebaseSignOut(auth);
}

export async function resetPassword(email: string): Promise<void> {
  const auth = getFirebaseAuth();
  await sendPasswordResetEmail(auth, email);
}

export async function verifyResetCode(code: string): Promise<string> {
  const auth = getFirebaseAuth();
  return firebaseVerifyPasswordResetCode(auth, code);
}

export async function confirmReset(code: string, newPassword: string): Promise<void> {
  const auth = getFirebaseAuth();
  await firebaseConfirmPasswordReset(auth, code, newPassword);
}

export async function updateUserDisplayName(displayName: string): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user.');
  }
  await updateProfile(user, { displayName });
}

export function onAuthStateChange(callback: (user: User | null) => void): () => void {
  const auth = getFirebaseAuth();
  return onAuthStateChanged(auth, callback);
}

export function getCurrentUser(): User | null {
  const auth = getFirebaseAuth();
  return auth.currentUser;
}

/**
 * CAB-605 — force a refresh of the signed-in user's ID token.
 *
 * Custom claims (notably `tier`, written by the Stripe webhook in CAB-602)
 * only reach the client when the token is refreshed, which otherwise happens
 * about hourly. After a checkout that is far too slow: the user has paid, the
 * webhook has already raised their plan, and their browser is still carrying a
 * token that says `free`.
 *
 * Resolves to false rather than throwing when there is no signed-in user or the
 * refresh fails — this is a best-effort freshening, never a blocker. The server
 * has its own fallback (it re-reads the Auth record before refusing), so a
 * failure here degrades to "correct, one request later".
 */
export async function refreshIdToken(): Promise<boolean> {
  // getFirebaseAuth() throws when Firebase has not been initialised, so it has
  // to be inside the try — otherwise this returns a rejected promise and every
  // `void refreshIdToken()` call site becomes an unhandled rejection, which is
  // exactly how the test suite caught it.
  try {
    const user = getFirebaseAuth().currentUser;
    if (!user) return false;
    await user.getIdToken(true);
    return true;
  } catch {
    return false;
  }
}

export async function checkEmailExists(email: string): Promise<boolean> {
  const projectId = getFirebaseApp().options.projectId;
  if (!projectId) {
    throw new Error('Firebase project ID is not configured.');
  }
  const url = new URL(`https://us-central1-${projectId}.cloudfunctions.net/checkEmailExists`);
  url.searchParams.set('email', email);
  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`checkEmailExists failed with status ${String(response.status)}`);
  }
  const data: unknown = await response.json();
  if (!isCheckEmailExistsResponse(data)) {
    throw new Error('checkEmailExists returned an unexpected response shape.');
  }
  return data.exists;
}

export async function changePassword(
  email: string,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const auth = getFirebaseAuth();
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user.');
  }
  const credential = EmailAuthProvider.credential(email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await firebaseUpdatePassword(user, newPassword);
}
